import { useState } from 'react';
import { SiOpenai } from 'react-icons/si';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { v4 } from '@rotra-air-ocean/rotranext-shared-code';
import { CircleAlert, Ellipsis, Play, Plus, Trash2 } from 'lucide-react';

import { contextSourceLabel } from '$layouts/v2/components/ContextData';
import { KIND_META, KindIcon } from '$layouts/v2/consts';
import {
  buildStatePreview,
  type FieldInfo,
  fieldTypeLabel,
  findField,
  producedBy,
} from '$layouts/v2/helpers/context';

import { Stack } from '../Stack';
import type { AdminMeta } from '../types';

import { FieldsEditor } from './fields/FieldsEditor';
import { ContextPanel, DataInSection } from './step-form/ContextControls';
import {
  ParsedDocumentBindingForm,
  ServiceDocumentBindingForm,
} from './step-form/DocumentBindingForm';
import { OutlookCategoryForm } from './step-form/OutlookCategoryForm';
import {
  ReadOnlyTextField,
  StepSection,
  TemplateField,
  Transitions,
  TransitionTargetSelect,
} from './step-form/StepFormPrimitives';
import { CasesEditor, KeyValueEditor } from './step-form/StepValueEditors';
import {
  SubWorkflowInputs,
  SubWorkflowOutputs,
} from './step-form/SubWorkflowMappings';
import { SystemOneQuestionsEditor } from './step-form/SystemOneQuestionsEditor';

export interface StepFormProps {
  id: string;
  step: v4.StepSpec;
  meta: AdminMeta;
  /** Workflow steps, including friendly labels for route destinations. */
  stepOptions: { id: string; label: string; kind: v4.StepKind }[];
  workflowIds: string[];
  availableFields: FieldInfo[];
  warnings: string[];
  isInitial: boolean;
  /** Shows this step's configuration without mutation controls. */
  readOnly?: boolean;
  /** Declared inputs of the referenced sub-workflow (subWorkflow steps only). */
  subInputs?: v4.FieldSpec[];
  /** Declared outputs of the referenced sub-workflow (subWorkflow steps only). */
  subOutputs?: v4.FieldSpec[];
  /** Active and archived versions available for a sub-workflow step. */
  subVersions?: v4.WorkflowVersionSpec[];
  subActiveVersion?: number | null;
  onPatch: (patch: Partial<v4.StepSpec>) => void;
  onRemove: () => void;
}

export function StepForm({
  id,
  step,
  meta,
  stepOptions,
  workflowIds,
  availableFields,
  warnings,
  isInitial,
  readOnly = false,
  subInputs,
  subOutputs,
  subVersions,
  subActiveVersion,
  onPatch,
  onRemove,
}: StepFormProps) {
  const kindMeta = KIND_META[step.kind];
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const selectFieldInfo = findField(
    availableFields,
    'selectField' in step ? step.selectField : undefined,
  );
  const inputFieldNames = [
    ...new Set([
      ...availableFields.map((field) => field.name),
      ...('inputFields' in step ? step.inputFields ?? [] : []),
      ...('stateFields' in step ? step.stateFields ?? [] : []),
    ]),
  ];
  const ownOutputs = producedBy(id, step);
  const hasWarnings = warnings.length > 0;
  let subVersionHelper: string;
  if (subVersions === undefined) {
    subVersionHelper = 'Loading available versions…';
  } else if (subVersions.length === 0) {
    subVersionHelper = 'No active or archived versions are available.';
  } else if (subActiveVersion == null) {
    subVersionHelper = 'Choose an archived version.';
  } else {
    subVersionHelper = `Active v${subActiveVersion} is recommended.`;
  }

  return (
    <Stack spacing={2.5} className="wf-step-form">
      <Box
        sx={{
          border: 1,
          borderColor: hasWarnings ? 'warning.main' : 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
          sx={{ p: 1.5 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 1.25,
                color: kindMeta.color,
                bgcolor: `${kindMeta.color}16`,
              }}
            >
              <KindIcon kind={step.kind} size={18} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                {step.label || 'Untitled step'}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {kindMeta.label}
                </Typography>
                <Typography
                  component="code"
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                  }}
                >
                  {id}
                </Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {isInitial && (
              <Chip
                size="small"
                color="primary"
                icon={<Play size={11} />}
                label="Starts here"
              />
            )}
            {readOnly && <Chip size="small" label="Read-only" />}
            {hasWarnings && (
              <Chip
                size="small"
                color="warning"
                icon={<CircleAlert size={11} />}
                label={
                  readOnly
                    ? `${warnings.length} warnings`
                    : `${warnings.length} to finish`
                }
              />
            )}
            {!readOnly && (
              <IconButton
                size="small"
                aria-label="Step actions"
                onClick={(event) => setMoreAnchor(event.currentTarget)}
              >
                <Ellipsis size={18} />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Box sx={{ p: 1.5 }}>
          <TextField
            label="Step name"
            value={step.label}
            onChange={
              readOnly
                ? undefined
                : (event) => onPatch({ label: event.target.value })
            }
            placeholder="For example: Classify incoming email"
            slotProps={{ htmlInput: { readOnly } }}
          />
          <ContextPanel fields={availableFields} />
        </Box>
      </Box>

      {!readOnly && (
        <Menu
          anchorEl={moreAnchor}
          open={Boolean(moreAnchor)}
          onClose={() => setMoreAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMoreAnchor(null);
              onRemove();
            }}
            sx={{ color: 'error.main' }}
          >
            <Trash2 size={15} />
            &nbsp; Delete step
          </MenuItem>
        </Menu>
      )}

      {hasWarnings && (
        <Alert severity="warning" variant="outlined">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {(step.kind === 'auto' || step.kind === 'apiCall') && (
        <>
          <StepSection
            title="What this step does"
            description="Choose the service to run and the message people see while it works."
          >
            {readOnly ? (
              <ReadOnlyTextField
                label="Service to run"
                value={step.serviceId}
                emptyLabel="No service selected"
              />
            ) : (
              <TextField
                select
                label="Service to run"
                value={step.serviceId ?? ''}
                onChange={(event) => onPatch({ serviceId: event.target.value })}
              >
                <MenuItem value="">Choose a service</MenuItem>
                {meta.serviceIds.map((serviceId) => (
                  <MenuItem key={serviceId} value={serviceId}>
                    {serviceId}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TemplateField
              label="Working message"
              helpText="Shown while the service is running."
              value={step.activity ?? ''}
              fields={availableFields}
              onChange={(activity) => onPatch({ activity })}
              multiline={false}
              readOnly={readOnly}
            />
          </StepSection>
          <ServiceDocumentBindingForm
            value={step.documents}
            capabilities={
              meta.serviceDocumentCapabilities?.[step.serviceId] ?? []
            }
            readOnly={readOnly}
            onChange={(documents) => onPatch({ documents })}
          />
          <Transitions description="Choose where the workflow should continue when this is finished.">
            <TransitionTargetSelect
              stepOptions={stepOptions}
              label="Then continue to"
              value={step.next}
              onChange={(next) => onPatch({ next })}
              readOnly={readOnly}
            />
          </Transitions>
        </>
      )}

      {step.kind === 'userInput' && (
        <>
          <StepSection
            title="Ask for information"
            description="Write a clear request for the person completing this workflow."
          >
            <TemplateField
              label="Message to the user"
              value={step.prompt ?? ''}
              fields={availableFields}
              onChange={(prompt) => onPatch({ prompt })}
              readOnly={readOnly}
            />
          </StepSection>
          <StepSection
            title="Data to collect"
            description="Each answer becomes available to the following steps."
          >
            <FieldsEditor
              label="Fields to collect"
              inputTypes={meta.fieldInputTypes}
              fields={step.fields ?? []}
              onChange={(fields) => onPatch({ fields })}
              readOnly={readOnly}
            />
          </StepSection>
          <StepSection
            title="Pre-fill answers"
            description="Optional. Use data already available in the workflow to suggest a starting value."
            optional
          >
            <KeyValueEditor
              label="Suggested values"
              value={step.defaults ?? {}}
              availableFields={availableFields}
              onChange={(defaults) => onPatch({ defaults })}
              readOnly={readOnly}
            />
          </StepSection>
          <Transitions description="After the information is submitted, continue to:">
            <TransitionTargetSelect
              stepOptions={stepOptions}
              label="Then continue to"
              value={step.next}
              onChange={(next) => onPatch({ next })}
              readOnly={readOnly}
            />
          </Transitions>
        </>
      )}

      {step.kind === 'llm' && (
        <>
          <StepSection
            title="What the AI should do"
            description="Choose a model and write the instruction in plain language."
          >
            {readOnly ? (
              <ReadOnlyTextField
                label="AI model"
                value={step.model}
                emptyLabel="No model selected"
              />
            ) : (
              <TextField
                select
                label="AI model"
                value={step.model ?? ''}
                onChange={(event) => onPatch({ model: event.target.value })}
              >
                <MenuItem value="">Choose a model</MenuItem>
                {meta.models.map((model) => (
                  <MenuItem key={model} value={model}>
                    <Box
                      component="span"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      <SiOpenai aria-label="OpenAI" />
                      <Box component="span" sx={{ flexGrow: 1 }}>
                        {model}
                      </Box>
                      {model === 'gpt-6-luna' && (
                        <Chip
                          label="Recommended"
                          size="small"
                          color="success"
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={step.webSearch ?? false}
                  disabled={readOnly}
                  onChange={(event) =>
                    onPatch({ webSearch: event.target.checked || undefined })
                  }
                />
              }
              label="Allow web search"
            />
            <TemplateField
              label="Instruction"
              helpText="Tell the model what to analyse and how to respond."
              value={step.prompt ?? ''}
              fields={availableFields}
              onChange={(prompt) => onPatch({ prompt })}
              rows={4}
              readOnly={readOnly}
            />
            <TemplateField
              label="Working message"
              helpText="Shown while the AI is working."
              value={step.activity ?? ''}
              fields={availableFields}
              onChange={(activity) => onPatch({ activity })}
              multiline={false}
              readOnly={readOnly}
            />
          </StepSection>
          <DataInSection
            title="Data sent to the AI"
            description="Choose only the information this step needs."
            availableFields={availableFields}
            inputFieldNames={inputFieldNames}
            selected={step.inputFields ?? inputFieldNames}
            onChange={(inputFields) => onPatch({ inputFields })}
            readOnly={readOnly}
          />
          <ParsedDocumentBindingForm
            value={step.documents}
            readOnly={readOnly}
            onChange={(documents) => onPatch({ documents })}
          />
          <StepSection
            title="Data returned by the AI"
            description="Define the information the AI must return for later steps."
          >
            <FieldsEditor
              label="Output fields"
              inputTypes={meta.fieldInputTypes}
              fields={step.output ?? []}
              onChange={(output) => onPatch({ output })}
              readOnly={readOnly}
            />
          </StepSection>
          <StepSection
            title="Completion message"
            description="Optional. Tell the user what happened after this step finishes."
            optional
          >
            <TemplateField
              label="Message shown when complete"
              value={step.summary ?? ''}
              fields={availableFields}
              extra={ownOutputs}
              onChange={(summary) => onPatch({ summary })}
              readOnly={readOnly}
            />
          </StepSection>
          <Transitions description="When the AI has responded, continue to:">
            <TransitionTargetSelect
              stepOptions={stepOptions}
              label="Then continue to"
              value={step.next}
              onChange={(next) => onPatch({ next })}
              readOnly={readOnly}
            />
          </Transitions>
        </>
      )}

      {step.kind === 'systemOne' && (
        <>
          <StepSection
            title="What to evaluate"
            description="Choose a model. Its working message is shown while the questions are evaluated."
          >
            {readOnly ? (
              <ReadOnlyTextField
                label="Model"
                value={step.model}
                emptyLabel="No model selected"
              />
            ) : (
              <TextField
                select
                label="Model"
                value={step.model ?? ''}
                onChange={(event) => onPatch({ model: event.target.value })}
              >
                <MenuItem value="">Choose a model</MenuItem>
                {meta.systemOneModels.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TemplateField
              label="Working message"
              helpText="Shown while the model is working."
              value={step.activity ?? ''}
              fields={availableFields}
              onChange={(activity) => onPatch({ activity })}
              multiline={false}
              readOnly={readOnly}
            />
          </StepSection>
          <DataInSection
            title="State — data to evaluate"
            description="Choose the fields sent to the model as the state JSON object."
            availableFields={availableFields}
            inputFieldNames={inputFieldNames}
            selected={step.stateFields ?? inputFieldNames}
            onChange={(stateFields) => onPatch({ stateFields })}
            readOnly={readOnly}
          />
          <StepSection
            title="State preview"
            description="The JSON object that will be sent as state, using placeholder values."
          >
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.25,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: 12,
                fontFamily: 'monospace',
                overflowX: 'auto',
              }}
            >
              {JSON.stringify(
                buildStatePreview(
                  availableFields,
                  step.stateFields ?? inputFieldNames,
                ),
                null,
                2,
              )}
            </Box>
          </StepSection>
          <ParsedDocumentBindingForm
            value={step.documents}
            readOnly={readOnly}
            onChange={(documents) => onPatch({ documents })}
          />
          <StepSection
            title="Questions"
            description="Each question is evaluated in parallel in a single call; its answer joins the context under the question id."
          >
            <SystemOneQuestionsEditor
              questions={step.questions ?? {}}
              onChange={(questions) => onPatch({ questions })}
              readOnly={readOnly}
            />
          </StepSection>
          <StepSection
            title="Completion message"
            description="Optional. Tell the user what happened after this step finishes."
            optional
          >
            <TemplateField
              label="Message shown when complete"
              value={step.summary ?? ''}
              fields={availableFields}
              extra={ownOutputs}
              onChange={(summary) => onPatch({ summary })}
              readOnly={readOnly}
            />
          </StepSection>
          <Transitions description="When the model has responded, continue to:">
            <TransitionTargetSelect
              stepOptions={stepOptions}
              label="Then continue to"
              value={step.next}
              onChange={(next) => onPatch({ next })}
              readOnly={readOnly}
            />
          </Transitions>
        </>
      )}

      {step.kind === 'outlookCategory' && (
        <OutlookCategoryForm
          step={step}
          availableFields={availableFields}
          stepOptions={stepOptions}
          readOnly={readOnly}
          onPatch={onPatch}
        />
      )}

      {step.kind === 'branch' && (
        <BranchRoutingCard
          id={id}
          step={step}
          availableFields={availableFields}
          selectFieldInfo={selectFieldInfo}
          stepIds={stepOptions.map((opt) => opt.id)}
          stepOptions={stepOptions}
          readOnly={readOnly}
          onPatch={onPatch}
        />
      )}

      {step.kind === 'decision' && (
        <>
          <StepSection
            title="Ask for a decision"
            description="Write the question or instruction shown to the person."
          >
            <TemplateField
              label="Question or message"
              value={step.prompt ?? ''}
              fields={availableFields}
              onChange={(prompt) => onPatch({ prompt })}
              readOnly={readOnly}
            />
          </StepSection>
          <Transitions description="For each choice, decide where the workflow should go next.">
            {step.actions.map((action, actionIndex) => (
              <Box
                key={actionIndex}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: readOnly
                    ? 'minmax(0, 1fr) minmax(0, 1fr)'
                    : 'minmax(0, 1fr) minmax(0, 1fr) auto',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <TextField
                  label="Choice label"
                  value={action.label ?? ''}
                  onChange={
                    readOnly
                      ? undefined
                      : (event) =>
                          onPatch({
                            actions: step.actions.map((currentAction, i) =>
                              i === actionIndex
                                ? {
                                    ...currentAction,
                                    label: event.target.value,
                                  }
                                : currentAction,
                            ),
                          })
                  }
                  slotProps={{ htmlInput: { readOnly } }}
                />
                <TransitionTargetSelect
                  stepOptions={stepOptions}
                  label="Then continue to"
                  value={action.next}
                  onChange={(next) =>
                    onPatch({
                      actions: step.actions.map((currentAction, i) =>
                        i === actionIndex
                          ? { ...currentAction, next }
                          : currentAction,
                      ),
                    })
                  }
                  readOnly={readOnly}
                />
                {!readOnly && (
                  <Button
                    color="error"
                    size="small"
                    aria-label={`Remove ${action.label || 'choice'}`}
                    onClick={() =>
                      onPatch({
                        actions: step.actions.filter(
                          (_, i) => i !== actionIndex,
                        ),
                      })
                    }
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </Box>
            ))}
            {!readOnly && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Plus size={14} />}
                onClick={() =>
                  onPatch({
                    actions: [...step.actions, { label: '', next: '' }],
                  })
                }
              >
                Add action
              </Button>
            )}
          </Transitions>
        </>
      )}

      {step.kind === 'subWorkflow' && (
        <>
          <StepSection
            title="Choose a sub-workflow"
            description="This step runs another workflow and returns here when it has finished."
          >
            {readOnly ? (
              <Stack spacing={1.5}>
                <ReadOnlyTextField
                  label="Workflow to run"
                  value={step.workflowId}
                  emptyLabel="No workflow selected"
                />
                <ReadOnlyTextField
                  label="Workflow version"
                  value={
                    step.workflowVersion == null
                      ? ''
                      : `v${step.workflowVersion} — ${
                          subVersions?.find(
                            (version) =>
                              version.version === step.workflowVersion,
                          )?.status ?? 'unavailable'
                        }`
                  }
                  emptyLabel="No version selected"
                />
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <TextField
                  select
                  label="Workflow to run"
                  value={step.workflowId ?? ''}
                  onChange={(event) =>
                    onPatch({
                      workflowId: event.target.value,
                      workflowVersion: undefined,
                      inputs: {},
                      outputs: {},
                    })
                  }
                >
                  <MenuItem value="">Choose a workflow</MenuItem>
                  {[
                    ...new Set([
                      ...workflowIds,
                      ...(step.workflowId ? [step.workflowId] : []),
                    ]),
                  ].map((workflowId) => (
                    <MenuItem key={workflowId} value={workflowId}>
                      {workflowId}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  required
                  disabled={!step.workflowId || subVersions === undefined}
                  label="Workflow version"
                  value={step.workflowVersion ?? ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    onPatch({
                      workflowVersion: value === '' ? undefined : Number(value),
                    });
                  }}
                  helperText={subVersionHelper}
                >
                  <MenuItem value="">Choose a version</MenuItem>
                  {(subVersions ?? []).map((version) => (
                    <MenuItem key={version.version} value={version.version}>
                      v{version.version} —{' '}
                      {version.version === subActiveVersion
                        ? 'Active (recommended)'
                        : 'Archived'}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            )}
          </StepSection>
          {step.workflowId && step.workflowVersion != null && (
            <StepSection
              title="Data sent to the sub-workflow"
              description="Map the information it needs from the data available here."
            >
              <SubWorkflowInputs
                inputs={subInputs}
                mapping={step.inputs ?? {}}
                availableFields={availableFields}
                onChange={(inputs) => onPatch({ inputs })}
                readOnly={readOnly}
              />
            </StepSection>
          )}
          {step.workflowId && step.workflowVersion != null && (
            <StepSection
              title="Data returned to this workflow"
              description="Choose where to store the results for later steps."
              optional
            >
              <SubWorkflowOutputs
                outputs={subOutputs}
                mapping={step.outputs ?? {}}
                availableFields={availableFields}
                onChange={(outputs) => onPatch({ outputs })}
                readOnly={readOnly}
              />
            </StepSection>
          )}
          <Transitions description="When the sub-workflow is complete, continue to:">
            <TransitionTargetSelect
              stepOptions={stepOptions}
              label="Then continue to"
              value={step.onDone}
              onChange={(onDone) => onPatch({ onDone })}
              readOnly={readOnly}
            />
          </Transitions>
        </>
      )}

      {step.kind === 'final' && (
        <StepSection
          title="Finish the workflow"
          description="Optionally leave a clear completion message for the person following the workflow."
          optional
        >
          <TemplateField
            label="Completion message"
            value={step.message ?? ''}
            fields={availableFields}
            onChange={(message) => onPatch({ message })}
            readOnly={readOnly}
          />
        </StepSection>
      )}
    </Stack>
  );
}

/** A branch-specific routing surface; unlike normal transitions, every path is conditional. */
function BranchRoutingCard({
  id,
  step,
  availableFields,
  selectFieldInfo,
  stepIds,
  stepOptions,
  readOnly,
  onPatch,
}: {
  id: string;
  step: v4.BranchStepSpec;
  availableFields: FieldInfo[];
  selectFieldInfo: FieldInfo | undefined;
  stepIds: string[];
  stepOptions: { id: string; label: string }[];
  readOnly: boolean;
  onPatch: (patch: Partial<v4.StepSpec>) => void;
}) {
  const scalarFields = availableFields.filter(
    (field) => field.type !== 'array' && field.type !== 'object',
  );
  const selectedScalarField = scalarFields.find(
    (field) => field.name === step.selectField,
  );
  const hasComplexField =
    selectFieldInfo?.type === 'array' || selectFieldInfo?.type === 'object';
  const canShowRoutes = Boolean(step.selectField);

  return (
    <Transitions>
      <Stack spacing={1.5}>
        {readOnly ? (
          <ReadOnlyTextField
            label="Which field to route by"
            value={step.selectField}
            emptyLabel="No field selected"
          />
        ) : (
          <Autocomplete<FieldInfo, false, false, true>
            id={`branch-field-${id}`}
            freeSolo
            options={scalarFields}
            groupBy={(option) => contextSourceLabel(option)}
            value={selectedScalarField ?? step.selectField ?? ''}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            isOptionEqualToValue={(option, value) =>
              typeof value === 'string'
                ? option.name === value
                : option.name === value.name
            }
            onChange={(_, value) =>
              onPatch({
                selectField:
                  typeof value === 'string' ? value : value?.name ?? '',
              })
            }
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                onPatch({ selectField: value });
              }
            }}
            renderOption={(optionProps, option) => (
              <Box component="li" {...optionProps} key={option.name}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {option.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {branchFieldSummary(option)} · {contextSourceLabel(option)}
                  </Typography>
                </Box>
              </Box>
            )}
            renderGroup={(params) => (
              <Box component="li" key={params.key} sx={{ '& > ul': { p: 0 } }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    px: 1.5,
                    pt: 1,
                    pb: 0.25,
                    fontWeight: 700,
                  }}
                >
                  {params.group}
                </Typography>
                <Box component="ul" sx={{ m: 0, p: 0 }}>
                  {params.children}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Which field to route by"
                placeholder="Choose a field"
              />
            )}
          />
        )}

        {hasComplexField && (
          <Typography
            variant="caption"
            sx={{
              color: 'warning.main',
            }}
          >
            Choose a single-value field to route.
          </Typography>
        )}

        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
          {canShowRoutes ? (
            <CasesEditor
              cases={step.cases ?? {}}
              stepIds={stepIds}
              stepOptions={stepOptions}
              fieldName={step.selectField}
              enumOptions={selectFieldInfo?.options}
              fallbackTarget={step.default}
              onChange={(cases) => onPatch({ cases })}
              readOnly={readOnly || hasComplexField}
            />
          ) : (
            <Box
              sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderStyle: 'dashed',
                borderRadius: 1,
                bgcolor: 'background.default',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Choose a field to add routes.
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            pt: 1.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Default
          </Typography>
          <TransitionTargetSelect
            stepOptions={stepOptions}
            label="Continue with"
            value={step.default}
            onChange={(next) => onPatch({ default: next })}
            readOnly={readOnly}
          />
          {!step.default && (
            <Typography
              variant="caption"
              sx={{
                color: 'warning.main',
                display: 'block',
                mt: 0.75,
              }}
            >
              No default — unmatched values fail.
            </Typography>
          )}
        </Box>
      </Stack>
    </Transitions>
  );
}

function branchFieldSummary(field: FieldInfo) {
  if (field.type === 'select') {
    const count = field.options?.length ?? 0;
    return `${count} ${count === 1 ? 'possible value' : 'possible values'}`;
  }
  return fieldTypeLabel(field);
}
